import { currentUser, Permission, UserDto, UserLoginResponseDto } from "@/api";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export interface AuthContextType {
  user: UserDto | null;
  permissions: Permission[] | null;
  isLoading: boolean;
  onLogin: (user: UserDto, permessi: Permission[]) => void;
  onUserUpdated: (user: UserDto) => void;
  onLogout: () => void;
  hasPermission: (permission: Permission) => boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  permissions: null,
  isLoading: false,
  onLogin: () => {},
  onUserUpdated: () => {},
  onLogout: () => {},
  hasPermission: () => false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  let [user, setUser] = useState<UserDto | null>(null);
  let [permissions, setPermissions] = useState<Permission[] | null>(null);
  let [isLoading, setLoading] = useState<boolean>(true);

  const queryClient = useQueryClient();

  // load initial state
  useEffect(() => {
    let unmounted = false;
    let start = new Date();
    const load = async () => {
      let result: AxiosResponse<UserLoginResponseDto, any> | null = null;
      try {
        result = await currentUser();
      } catch (err) {
        console.log(err);
      }

      if (unmounted) {
        return;
      }

      const MIN_LOADING_TIME = 300;

      const diff = new Date().getTime() - start.getTime();
      const timeout = diff < MIN_LOADING_TIME ? MIN_LOADING_TIME - diff : 1;

      setTimeout(() => {
        if (unmounted) {
          return;
        }

        const user = result?.data?.user;
        if (user) {
          setUser(user);
        }

        const userPermissions: Permission[] = result?.data?.permissions ?? [];
        setPermissions(userPermissions);

        setLoading(false);
      }, timeout);
    };

    load();

    return () => {
      unmounted = true;
    };
  }, []);

  let onLogin = useCallback(
    (newUser: UserDto, permessi: Permission[]) => {
      setUser(newUser);
      setPermissions(permessi);
    },
    [setUser]
  );

  let onLogout = useCallback(() => {
    queryClient.invalidateQueries();
    queryClient.clear();
    setUser(null);
    setPermissions(null);
  }, [setUser, queryClient]);

  let hasPermission = useCallback(
    (permission: Permission) => {
      if (permissions) {
        return permissions.includes(permission);
      } else {
        return false;
      }
    },
    [permissions]
  );

  let value = {
    user,
    permissions,
    isLoading,
    onLogin,
    onUserUpdated: useCallback(
      (updatedUser: UserDto) => {
        onLogin(updatedUser, permissions ?? []);
      },
      [onLogin, permissions]
    ),
    onLogout,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
